import Authorization from './Authorization';

// Minimal local types for the Heidi widget (https://www.heidihealth.com/developers/widget/sdk-specification/types-definition)
export interface HeidiPatientInfo {
    id?: string;
    name?: string;
    gender?: string;
    dob?: string;
}

export interface HeidiPushData {
    noteData: string | object;
    transcript?: string;
    sessionId: string;
    patientInfo?: HeidiPatientInfo;
}

export interface HeidiOpenParams {
    patient?: HeidiPatientInfo;
    sessionId?: string;
    startNewSession?: boolean;
    context?: string;
}

interface HeidiDisplayOptions {
    fitToWindow?: boolean;
    expandable?: boolean;
    maxHeight?: number;
}

interface HeidiInitOptions {
    token: string;
    target: string;
    region?: string;
    display?: HeidiDisplayOptions;
    onInit?: () => void;
    onReady?: () => void;
}

interface HeidiStatic {
    open: (params?: HeidiOpenParams) => void;
    close: (params?: { keepSession?: boolean; force?: boolean }) => void;
    setToken: (token: string) => void;
    onPushData: (callback: (data: HeidiPushData) => void) => void;
    onSessionStarted: (callback: (sessionId: string) => void) => void;
    onTokenExpired: (callback: () => void) => void;
    onClose: (callback: () => void) => void;
    onResize: (callback: (expanded: boolean) => void) => void;
    new (options: HeidiInitOptions): unknown;
}

declare global {
    interface Window {
        Heidi?: HeidiStatic;
    }
}

export const HEIDI_WIDGET_TARGET_ID = 'heidi-widget-container';
const HEIDI_STYLE_READY_CLASS = 'heidi-style-ready';
const HEIDI_CLOSE_BUTTON_ID = 'heidi-widget-close-btn';
const HEIDI_CLOSE_BUTTON_VISIBLE_CLASS = 'is-visible';

export interface HeidiWidgetHandlers {
    onSessionStarted?: (sessionId: string) => void;
    onPushData?: (data: HeidiPushData) => void;
    onTokenExpired?: () => void;
}

// Staging widget pairs with tokens from registrar.api.heidihealth.com (Local/Dev/QA) and must
// NOT set a region, or it authenticates against the region-specific registrar and gets a 401.
// The production widget pairs with tokens from us.api.heidihealth.com and requires region 'US'.
// See https://www.heidihealth.com/developers/widget/going-live
const isProductionEnv = () => new Authorization().getEnv() === 'Production';

const getWidgetScriptUrl = () =>
    isProductionEnv() ? 'https://widget.heidihealth.com/widget/heidi.js' : 'https://registrar.widget.heidihealth.com/staging/widget/heidi.js';

const getWidgetRegion = () => (isProductionEnv() ? 'US' : undefined);
const getWidgetDisplayOptions = (): HeidiDisplayOptions => ({
    // Full viewport height on open. Compact width is applied in JS so expand/collapse can still work.
    fitToWindow: true,
    expandable: true,
});

// Handlers are held in a mutable ref so re-initializing refreshes them without re-registering widget callbacks.
const handlers: HeidiWidgetHandlers = {};

let scriptPromise: Promise<void> | null = null;
let widgetReadyPromise: Promise<void> | null = null;
let iframeEverLoaded = false;
let callbacksRegistered = false;
/** Ignore fitToWindow's expanded=true events briefly after open so we can start at compact width. */
let ignoreExpandedUntilMs = 0;
let styleObserver: MutationObserver | null = null;
let settleTimer: ReturnType<typeof setTimeout> | null = null;
let missedLoadTimer: ReturnType<typeof setTimeout> | null = null;
let overallReadyTimer: ReturnType<typeof setTimeout> | null = null;
let closeButtonResizeObserver: ResizeObserver | null = null;

/** Extra time after iframe load for Heidi's UI to paint before we reveal border + iframe. */
const POST_LOAD_SETTLE_MS = 600;
/** If the load event was already missed (iframe created during init), reveal after this. */
const MISSED_LOAD_FALLBACK_MS = 2500;
/** Safety net if the iframe never appears. */
const OVERALL_READY_TIMEOUT_MS = 15000;

// The container lives on document.body (not inside a React component) because the widget
// initializes only once; a React-rendered target would be destroyed on unmount and the
// widget would stay bound to a detached DOM node.
const ensureWidgetContainer = () => {
    if (!document.getElementById(HEIDI_WIDGET_TARGET_ID)) {
        const container = document.createElement('div');
        container.id = HEIDI_WIDGET_TARGET_ID;
        document.body.append(container);
    }
};

const getWidgetIframe = () => document.querySelector<HTMLIFrameElement>(`#${HEIDI_WIDGET_TARGET_ID} iframe.heidi-iframe`);

/** Close control is overlaid on the host page — Heidi's iframe is cross-origin so we cannot inject into it. */
const ensureCloseButton = () => {
    let button = document.getElementById(HEIDI_CLOSE_BUTTON_ID) as HTMLButtonElement | null;
    if (!button) {
        button = document.createElement('button');
        button.id = HEIDI_CLOSE_BUTTON_ID;
        button.type = 'button';
        button.setAttribute('aria-label', 'Close Heidi widget');
        button.title = 'Close';
        button.textContent = '×';
        // Soft close so Heidi can confirm if a recording is in progress.
        button.addEventListener('click', () => {
            window.Heidi?.close();
        });
        document.body.append(button);
    }
    return button;
};

const positionCloseButton = () => {
    const iframe = getWidgetIframe();
    const button = document.getElementById(HEIDI_CLOSE_BUTTON_ID);
    if (!iframe || !button || !button.classList.contains(HEIDI_CLOSE_BUTTON_VISIBLE_CLASS)) {
        return;
    }

    const rect = iframe.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
        return;
    }

    // Top of the widget, inset from the right so it doesn't cover Heidi's own controls.
    const size = 32;
    const topInset = 12;
    const rightInset = 75;
    button.style.top = `${Math.max(8, rect.top + topInset)}px`;
    button.style.left = `${Math.max(8, rect.right - rightInset - size)}px`;
};

const getCompactWidth = () => (window.matchMedia('(max-width: 768px)').matches ? '92vw' : '33vw');

/** Apply host compact width without a permanent CSS !important rule (that breaks Heidi resize). */
const applyCompactWidth = () => {
    const iframe = getWidgetIframe();
    if (!iframe) {
        return;
    }
    const width = getCompactWidth();
    iframe.style.setProperty('width', width, 'important');
    iframe.style.setProperty('max-width', width, 'important');
    requestAnimationFrame(() => positionCloseButton());
};

const applyExpandedWidth = () => {
    const iframe = getWidgetIframe();
    if (!iframe) {
        return;
    }
    // Match fitToWindow width; leave height to Heidi so minimize can still shrink it.
    iframe.style.setProperty('width', '100vw', 'important');
    iframe.style.setProperty('max-width', '100vw', 'important');
    requestAnimationFrame(() => positionCloseButton());
};

const clearHostWidthOverrides = () => {
    const iframe = getWidgetIframe();
    if (!iframe) {
        return;
    }
    iframe.style.removeProperty('width');
    iframe.style.removeProperty('max-width');
};

const handleHeidiResize = (expanded: boolean) => {
    if (expanded) {
        if (Date.now() < ignoreExpandedUntilMs) {
            applyCompactWidth();
            return;
        }
        applyExpandedWidth();
        return;
    }

    applyCompactWidth();
};

const stopCloseButtonTracking = () => {
    closeButtonResizeObserver?.disconnect();
    closeButtonResizeObserver = null;
    window.removeEventListener('resize', positionCloseButton);
};

const hideCloseButton = () => {
    stopCloseButtonTracking();
    const button = document.getElementById(HEIDI_CLOSE_BUTTON_ID);
    if (button) {
        button.classList.remove(HEIDI_CLOSE_BUTTON_VISIBLE_CLASS);
    }
};

const showCloseButton = () => {
    const button = ensureCloseButton();
    button.classList.add(HEIDI_CLOSE_BUTTON_VISIBLE_CLASS);
    positionCloseButton();

    stopCloseButtonTracking();
    window.addEventListener('resize', positionCloseButton);
    const iframe = getWidgetIframe();
    if (iframe && typeof ResizeObserver !== 'undefined') {
        closeButtonResizeObserver = new ResizeObserver(() => positionCloseButton());
        closeButtonResizeObserver.observe(iframe);
    }
};

const clearReadyTimers = () => {
    if (settleTimer) {
        clearTimeout(settleTimer);
        settleTimer = null;
    }
    if (missedLoadTimer) {
        clearTimeout(missedLoadTimer);
        missedLoadTimer = null;
    }
    if (overallReadyTimer) {
        clearTimeout(overallReadyTimer);
        overallReadyTimer = null;
    }
};

const clearWidgetStyles = () => {
    styleObserver?.disconnect();
    styleObserver = null;
    clearReadyTimers();
    hideCloseButton();
    clearHostWidthOverrides();
    document.getElementById(HEIDI_WIDGET_TARGET_ID)?.classList.remove(HEIDI_STYLE_READY_CLASS);
};

const markWidgetStylesReady = () => {
    iframeEverLoaded = true;
    document.getElementById(HEIDI_WIDGET_TARGET_ID)?.classList.add(HEIDI_STYLE_READY_CLASS);
    // Full height from fitToWindow; start at compact width until the user maximizes.
    applyCompactWidth();
    showCloseButton();
};

/**
 * Resolves when the Heidi iframe has loaded and settled so custom styles can be shown
 * at the same time as the widget UI (iframe stays hidden until then).
 */
const waitForWidgetVisuallyReady = (): Promise<void> => {
    const container = document.getElementById(HEIDI_WIDGET_TARGET_ID);
    if (!container) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        let settled = false;

        const finish = () => {
            if (settled) {
                return;
            }
            settled = true;
            clearReadyTimers();
            styleObserver?.disconnect();
            styleObserver = null;
            markWidgetStylesReady();
            resolve();
        };

        const settleThenFinish = () => {
            if (missedLoadTimer) {
                clearTimeout(missedLoadTimer);
                missedLoadTimer = null;
            }
            if (settleTimer) {
                clearTimeout(settleTimer);
            }
            settleTimer = setTimeout(finish, POST_LOAD_SETTLE_MS);
        };

        const attachToIframe = (iframe: HTMLIFrameElement) => {
            // Re-open reuses a loaded iframe; load may not fire again.
            if (iframeEverLoaded) {
                settleThenFinish();
                return;
            }

            const onLoad = () => {
                settleThenFinish();
            };

            iframe.addEventListener('load', onLoad, { once: true });
            // Cross-origin iframes can finish loading before we attach the listener.
            missedLoadTimer = setTimeout(onLoad, MISSED_LOAD_FALLBACK_MS);
        };

        overallReadyTimer = setTimeout(finish, OVERALL_READY_TIMEOUT_MS);

        const existing = container.querySelector<HTMLIFrameElement>('iframe.heidi-iframe');
        if (existing) {
            attachToIframe(existing);
            return;
        }

        styleObserver = new MutationObserver(() => {
            const iframe = container.querySelector<HTMLIFrameElement>('iframe.heidi-iframe');
            if (iframe) {
                styleObserver?.disconnect();
                styleObserver = null;
                attachToIframe(iframe);
            }
        });
        styleObserver.observe(container, { childList: true, subtree: true });
    });
};

const loadWidgetScript = (): Promise<void> => {
    if (window.Heidi) {
        return Promise.resolve();
    }
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.async = true;
            script.src = getWidgetScriptUrl();
            script.addEventListener('load', () => resolve());
            script.addEventListener('error', () => {
                scriptPromise = null;
                reject(new Error('Unable to load the Heidi widget.'));
            });
            document.head.append(script);
        });
    }
    return scriptPromise;
};

const registerWidgetCallbacks = () => {
    if (callbacksRegistered) {
        return;
    }
    callbacksRegistered = true;
    window.Heidi?.onSessionStarted((sessionId) => handlers.onSessionStarted?.(sessionId));
    window.Heidi?.onPushData((data) => handlers.onPushData?.(data));
    window.Heidi?.onTokenExpired(() => handlers.onTokenExpired?.());
    window.Heidi?.onClose(() => {
        clearWidgetStyles();
    });
    window.Heidi?.onResize((expanded) => {
        handleHeidiResize(expanded);
    });
};

/**
 * Loads and initializes the Heidi widget (once), registering the provided handlers.
 * Subsequent calls refresh the token and handlers on the already-initialized widget.
 */
export const initHeidiWidget = (token: string, widgetHandlers: HeidiWidgetHandlers): Promise<void> => {
    handlers.onSessionStarted = widgetHandlers.onSessionStarted;
    handlers.onPushData = widgetHandlers.onPushData;
    handlers.onTokenExpired = widgetHandlers.onTokenExpired;

    if (widgetReadyPromise) {
        return widgetReadyPromise.then(() => {
            window.Heidi?.setToken(token);
        });
    }

    widgetReadyPromise = loadWidgetScript().then(
        () =>
            new Promise<void>((resolve, reject) => {
                ensureWidgetContainer();
                const timeout = window.setTimeout(() => {
                    reject(new Error('The Heidi widget did not finish initializing. Please try again.'));
                }, 20000);
                // Per the Heidi docs, onInit signals the widget can be opened; onReady signals
                // callbacks can be registered. Resolve on whichever fires first so open() is not
                // blocked if onReady arrives late.
                new window.Heidi({
                    token,
                    target: `#${HEIDI_WIDGET_TARGET_ID}`,
                    region: getWidgetRegion(),
                    display: getWidgetDisplayOptions(),
                    onInit: () => {
                        console.info('[HeidiWidget] onInit fired');
                        window.clearTimeout(timeout);
                        resolve();
                    },
                    onReady: () => {
                        console.info('[HeidiWidget] onReady fired');
                        window.clearTimeout(timeout);
                        registerWidgetCallbacks();
                        resolve();
                    },
                });
            })
    );
    widgetReadyPromise.catch(() => {
        widgetReadyPromise = null;
        callbacksRegistered = false;
    });
    return widgetReadyPromise;
};

/** Opens the widget and resolves once it is visually ready (border + cursor can update together). */
export const openHeidiWidget = (params?: HeidiOpenParams): Promise<void> => {
    console.info('[HeidiWidget] open called', { hasHeidi: Boolean(window.Heidi) });
    ignoreExpandedUntilMs = Date.now() + 1500;
    clearWidgetStyles();
    window.Heidi?.open(params);
    return waitForWidgetVisuallyReady();
};

export const setHeidiWidgetToken = (token: string) => {
    window.Heidi?.setToken(token);
};

export const closeHeidiWidget = () => {
    // Force-close skips Heidi's confirmation (used after Push Note). Overlay button uses soft close.
    window.Heidi?.close({ force: true });
    clearWidgetStyles();
};
